import { useState, useCallback, Suspense, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, PerspectiveCamera, Html } from "@react-three/drei";
import * as THREE from "three";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import { 
  Package, Plus, Trash2, Calculator, Box, Truck, Container, RotateCcw,
  Save, Download, Upload, Settings, Maximize2, Eye, EyeOff, Info, 
  ChevronRight, ChevronDown, AlertTriangle, CheckCircle, Loader2
} from "lucide-react";


// Container specs (matching backend)
const CONTAINER_SPECS = {
  "20st": { name: "20ft Standard", length: 589, width: 235, height: 239, max_weight: 28230, volume: 33.1 },
  "40st": { name: "40ft Standard", length: 1202, width: 235, height: 239, max_weight: 26700, volume: 67.5 },
  "40hc": { name: "40ft High Cube", length: 1202, width: 235, height: 269, max_weight: 26460, volume: 76.0 },
  "45hc": { name: "45ft High Cube", length: 1351, width: 235, height: 269, max_weight: 25600, volume: 85.7 },
  "truck_std": { name: "Standard Truck (13.6m)", length: 1360, width: 245, height: 270, max_weight: 24000, volume: 90.0 },
  "truck_mega": { name: "Mega Trailer (13.6m)", length: 1360, width: 245, height: 300, max_weight: 24000, volume: 100.0 }
};

// Preset cargo items
const PRESET_ITEMS = {
  euro_pallet: { name: "Euro Pallet", length: 120, width: 80, height: 144, weight: 500, cargo_type: "pallet" },
  us_pallet: { name: "US Pallet", length: 122, width: 102, height: 144, weight: 600, cargo_type: "pallet" },
  standard_box: { name: "Standard Box", length: 60, width: 40, height: 40, weight: 25, cargo_type: "box" },
  barrel: { name: "Barrel/Drum", length: 58, width: 58, height: 88, weight: 200, cargo_type: "barrel" },
  bigbag: { name: "Big Bag", length: 90, width: 90, height: 120, weight: 1000, cargo_type: "bigbag" }
};

// Color palette for cargo items
const CARGO_COLORS = [
  "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", 
  "#EC4899", "#06B6D4", "#84CC16", "#F97316", "#6366F1"
];

// 3D Container Component
function ContainerMesh({ dimensions, opacity = 0.08 }) {
  const scale = 0.01; // Convert cm to meters for visualization
  const l = dimensions.length * scale;
  const w = dimensions.width * scale;
  const h = dimensions.height * scale;
  
  return (
    <group>
      {/* Transparent container body - positioned from origin */}
      <mesh position={[l/2, h/2, w/2]}>
        <boxGeometry args={[l, h, w]} />
        <meshBasicMaterial color="#475569" transparent opacity={opacity} side={2} />
      </mesh>
      {/* Container edges using Box wireframe - white for visibility */}
      <lineSegments position={[l/2, h/2, w/2]}>
        <edgesGeometry args={[new THREE.BoxGeometry(l, h, w)]} />
        <lineBasicMaterial color="#94a3b8" linewidth={2} />
      </lineSegments>
      {/* Floor */}
      <mesh position={[l/2, 0.002, w/2]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[l, w]} />
        <meshBasicMaterial color="#1e293b" />
      </mesh>
    </group>
  );
}

// 3D Cargo Item Component
function CargoMesh({ item, showLabels }) {
  const scale = 0.01;
  const x = item.x * scale;
  const y = item.y * scale;
  const z = item.z * scale;
  const l = item.rotated_length * scale;
  const w = item.rotated_width * scale;
  const h = item.rotated_height * scale;
  
  const color = item.item?.color || "#3B82F6";
  
  // Position the box at x, y, z (bottom-left-back corner)
  // Then offset by half dimensions to center the geometry
  const posX = x + l/2;
  const posY = y + h/2;
  const posZ = z + w/2;
  
  return (
    <group position={[posX, posY, posZ]}>
      <mesh>
        <boxGeometry args={[l, h, w]} />
        <meshBasicMaterial 
          color={color} 
          transparent 
          opacity={0.92}
        />
      </mesh>
      {/* Wireframe edges */}
      <lineSegments>
        <edgesGeometry args={[new THREE.BoxGeometry(l, h, w)]} />
        <lineBasicMaterial color="#ffffff" linewidth={1} />
      </lineSegments>
      {showLabels && (
        <Html center distanceFactor={10}>
          <div className="bg-black/80 text-white text-xs px-1 py-0.5 rounded whitespace-nowrap pointer-events-none">
            {item.item?.name}
          </div>
        </Html>
      )}
    </group>
  );
}

// 3D Scene Component
function LoadVisualization({ result, selectedContainer, showLabels }) {
  const containerData = result?.containers?.[selectedContainer];
  if (!containerData) return null;
  
  const dimensions = containerData.container_dimensions;
  const scale = 0.01;
  const l = dimensions.length * scale;
  const w = dimensions.width * scale;
  const h = dimensions.height * scale;
  
  // Calculate camera position based on container size
  const maxDim = Math.max(l, w, h);
  
  return (
    <>
      {/* Strong ambient light for even illumination across entire container */}
      <ambientLight intensity={1.5} />
      
      {/* Multiple directional lights to cover long containers */}
      <directionalLight position={[l * 0.25, h * 3, w * 2]} intensity={1.0} />
      <directionalLight position={[l * 0.5, h * 3, w * 2]} intensity={1.0} />
      <directionalLight position={[l * 0.75, h * 3, w * 2]} intensity={1.0} />
      <directionalLight position={[l, h * 3, w * 2]} intensity={1.0} />
      
      {/* Back lights */}
      <directionalLight position={[l * 0.5, h * 2, -w]} intensity={0.8} />
      
      {/* Top light */}
      <directionalLight position={[l/2, h * 5, w/2]} intensity={0.8} />
      
      {/* Point lights distributed along container length */}
      <pointLight position={[l * 0.25, h * 2, w/2]} intensity={0.6} />
      <pointLight position={[l * 0.5, h * 2, w/2]} intensity={0.6} />
      <pointLight position={[l * 0.75, h * 2, w/2]} intensity={0.6} />
      
      <PerspectiveCamera 
        makeDefault 
        position={[l * 0.5, h * 3, w * 4]} 
        fov={50}
      />
      <OrbitControls 
        target={[l/2, h/2, w/2]} 
        enablePan={true}
        enableZoom={true}
        minDistance={1}
        maxDistance={maxDim * 5}
      />
      
      {/* Container */}
      <ContainerMesh dimensions={dimensions} />
      
      {/* Cargo items */}
      {containerData.placed_items?.map((item, idx) => (
        <CargoMesh key={idx} item={item} showLabels={showLabels} />
      ))}
      
      {/* Grid helper */}
      <gridHelper 
        args={[Math.max(l, w) * 1.5, 20, "#475569", "#334155"]} 
        position={[l/2, -0.01, w/2]} 
      />
    </>
  );
}

export default function LoadCalculatorPage() {
  const [cargoItems, setCargoItems] = useState([
    { id: 1, name: "Product Box", length: 60, width: 40, height: 40, weight: 25, quantity: 50, stackable: true, tiltable: true, fragile: false, cargo_type: "box", color: CARGO_COLORS[0] }
  ]);
  const [selectedContainers, setSelectedContainers] = useState(["20st", "40st", "40hc"]);
  const [unitSystem, setUnitSystem] = useState("metric");
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedContainerIndex, setSelectedContainerIndex] = useState(0);
  const [showLabels, setShowLabels] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  
  const nextId = useRef(2);

  // Add cargo item
  const addCargoItem = () => {
    const colorIndex = cargoItems.length % CARGO_COLORS.length;
    setCargoItems([...cargoItems, {
      id: nextId.current++,
      name: `Item ${nextId.current}`,
      length: 60,
      width: 40,
      height: 40,
      weight: 25,
      quantity: 1,
      stackable: true,
      tiltable: true,
      fragile: false,
      cargo_type: "box",
      color: CARGO_COLORS[colorIndex]
    }]);
  };

  // Add preset item
  const addPresetItem = (presetKey) => {
    const preset = PRESET_ITEMS[presetKey];
    const colorIndex = cargoItems.length % CARGO_COLORS.length;
    setCargoItems([...cargoItems, {
      id: nextId.current++,
      ...preset,
      quantity: 1,
      stackable: true,
      tiltable: true,
      fragile: false,
      color: CARGO_COLORS[colorIndex]
    }]);
  };

  // Update cargo item
  const updateCargoItem = (id, field, value) => {
    setCargoItems(items => items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  // Remove cargo item
  const removeCargoItem = (id) => {
    setCargoItems(items => items.filter(item => item.id !== id));
  };

  // Toggle container selection
  const toggleContainer = (containerType) => {
    setSelectedContainers(prev => {
      if (prev.includes(containerType)) {
        return prev.filter(c => c !== containerType);
      }
      return [...prev, containerType];
    });
  };

  // Calculate load
  const calculateLoad = async () => {
    if (cargoItems.length === 0) {
      toast.error("Add at least one cargo item");
      return;
    }
    if (selectedContainers.length === 0) {
      toast.error("Select at least one container type");
      return;
    }

    setCalculating(true);
    try {
      const response = await fetch(`${API_URL}/load-calculator/calculate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cargo_items: cargoItems,
          container_types: selectedContainers,
          unit_system: unitSystem
        })
      });

      if (!response.ok) {
        throw new Error("Calculation failed");
      }

      const data = await response.json();
      setResult(data);
      setSelectedContainerIndex(0);
      
      if (data.success) {
        toast.success(`Loaded ${data.total_items_loaded} items into ${data.total_containers} container(s)`);
      } else {
        toast.error(data.summary?.error || "Could not fit cargo");
      }
    } catch (error) {
      console.error("Calculation error:", error);
      toast.error("Failed to calculate load");
    } finally {
      setCalculating(false);
    }
  };

  // Reset
  const resetCalculator = () => {
    setCargoItems([{
      id: 1,
      name: "Product Box",
      length: 60,
      width: 40,
      height: 40,
      weight: 25,
      quantity: 50,
      stackable: true,
      tiltable: true,
      fragile: false,
      cargo_type: "box",
      color: CARGO_COLORS[0]
    }]);
    setResult(null);
    setSelectedContainerIndex(0);
    nextId.current = 2;
  };

  // Calculate total stats
  const totalStats = cargoItems.reduce((acc, item) => {
    const volume = (item.length * item.width * item.height * item.quantity) / 1000000; // m³
    const weight = item.weight * item.quantity;
    return {
      items: acc.items + item.quantity,
      volume: acc.volume + volume,
      weight: acc.weight + weight
    };
  }, { items: 0, volume: 0, weight: 0 });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Navbar />
      
      <div className="pt-20 px-4 pb-8 max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Container className="h-6 w-6 text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Loading & Stuffing Calculator</h1>
          </div>
          <p className="text-slate-400">
            Calculate optimal cargo placement in containers with 3D visualization
          </p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Left Panel - Input */}
          <div className="space-y-4">
            {/* Container Selection */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Box className="h-5 w-5 text-blue-400" />
                  Container Selection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(CONTAINER_SPECS).map(([key, spec]) => (
                    <div
                      key={key}
                      onClick={() => toggleContainer(key)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedContainers.includes(key)
                          ? "bg-blue-500/20 border-blue-500 text-white"
                          : "bg-slate-700/50 border-slate-600 text-slate-300 hover:border-slate-500"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {key.includes("truck") ? (
                          <Truck className="h-4 w-4" />
                        ) : (
                          <Container className="h-4 w-4" />
                        )}
                        <span className="text-sm font-medium">{spec.name}</span>
                      </div>
                      <div className="text-xs text-slate-400">
                        {spec.volume} m³ / {(spec.max_weight/1000).toFixed(1)}t
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Cargo Items */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Package className="h-5 w-5 text-green-400" />
                    Cargo Items
                  </CardTitle>
                  <div className="flex gap-2">
                    <Select onValueChange={addPresetItem}>
                      <SelectTrigger className="w-36 h-8 bg-slate-700 border-slate-600 text-sm">
                        <SelectValue placeholder="Add preset" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PRESET_ITEMS).map(([key, item]) => (
                          <SelectItem key={key} value={key}>{item.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button onClick={addCargoItem} size="sm" className="bg-green-600 hover:bg-green-700">
                      <Plus className="h-4 w-4 mr-1" /> Add
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[400px] overflow-y-auto">
                {cargoItems.map((item) => (
                  <div 
                    key={item.id} 
                    className="bg-slate-700/50 rounded-lg border border-slate-600 overflow-hidden"
                  >
                    {/* Item Header */}
                    <div 
                      className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-700/70"
                      onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                    >
                      <div 
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: item.color }}
                      />
                      <Input
                        value={item.name}
                        onChange={(e) => updateCargoItem(item.id, "name", e.target.value)}
                        className="flex-1 h-8 bg-slate-600/50 border-slate-500 text-white"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">
                          {item.length}×{item.width}×{item.height}cm
                        </span>
                        <span className="text-xs text-slate-400">×{item.quantity}</span>
                        {expandedItem === item.id ? (
                          <ChevronDown className="h-4 w-4 text-slate-400" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); removeCargoItem(item.id); }}
                          className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {expandedItem === item.id && (
                      <div className="p-3 pt-0 border-t border-slate-600">
                        <div className="grid grid-cols-4 gap-3 mb-3">
                          <div>
                            <Label className="text-xs text-slate-400">Length (cm)</Label>
                            <Input
                              type="number"
                              value={item.length}
                              onChange={(e) => updateCargoItem(item.id, "length", parseFloat(e.target.value) || 0)}
                              className="h-8 bg-slate-600/50 border-slate-500 text-white"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400">Width (cm)</Label>
                            <Input
                              type="number"
                              value={item.width}
                              onChange={(e) => updateCargoItem(item.id, "width", parseFloat(e.target.value) || 0)}
                              className="h-8 bg-slate-600/50 border-slate-500 text-white"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400">Height (cm)</Label>
                            <Input
                              type="number"
                              value={item.height}
                              onChange={(e) => updateCargoItem(item.id, "height", parseFloat(e.target.value) || 0)}
                              className="h-8 bg-slate-600/50 border-slate-500 text-white"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400">Weight (kg)</Label>
                            <Input
                              type="number"
                              value={item.weight}
                              onChange={(e) => updateCargoItem(item.id, "weight", parseFloat(e.target.value) || 0)}
                              className="h-8 bg-slate-600/50 border-slate-500 text-white"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-3 mb-3">
                          <div>
                            <Label className="text-xs text-slate-400">Quantity</Label>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateCargoItem(item.id, "quantity", parseInt(e.target.value) || 1)}
                              className="h-8 bg-slate-600/50 border-slate-500 text-white"
                              min="1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400">Type</Label>
                            <Select 
                              value={item.cargo_type} 
                              onValueChange={(v) => updateCargoItem(item.id, "cargo_type", v)}
                            >
                              <SelectTrigger className="h-8 bg-slate-600/50 border-slate-500">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="box">Box</SelectItem>
                                <SelectItem value="pallet">Pallet</SelectItem>
                                <SelectItem value="barrel">Barrel</SelectItem>
                                <SelectItem value="roll">Roll</SelectItem>
                                <SelectItem value="bigbag">Big Bag</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400">Color</Label>
                            <div className="flex gap-1 mt-1">
                              {CARGO_COLORS.slice(0, 5).map((color) => (
                                <div
                                  key={color}
                                  onClick={() => updateCargoItem(item.id, "color", color)}
                                  className={`w-6 h-6 rounded cursor-pointer border-2 ${
                                    item.color === color ? "border-white" : "border-transparent"
                                  }`}
                                  style={{ backgroundColor: color }}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 text-sm text-slate-300">
                            <Switch
                              checked={item.stackable}
                              onCheckedChange={(v) => updateCargoItem(item.id, "stackable", v)}
                            />
                            Stackable
                          </label>
                          <label className="flex items-center gap-2 text-sm text-slate-300">
                            <Switch
                              checked={item.tiltable}
                              onCheckedChange={(v) => updateCargoItem(item.id, "tiltable", v)}
                            />
                            Can Rotate
                          </label>
                          <label className="flex items-center gap-2 text-sm text-slate-300">
                            <Switch
                              checked={item.fragile}
                              onCheckedChange={(v) => updateCargoItem(item.id, "fragile", v)}
                            />
                            Fragile
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Total Stats & Calculate Button */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-white">{totalStats.items}</div>
                      <div className="text-xs text-slate-400">Total Items</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-400">{totalStats.volume.toFixed(2)} m³</div>
                      <div className="text-xs text-slate-400">Total Volume</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-400">{(totalStats.weight/1000).toFixed(2)} t</div>
                      <div className="text-xs text-slate-400">Total Weight</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={resetCalculator} className="border-slate-600 text-slate-300">
                      <RotateCcw className="h-4 w-4 mr-1" /> Reset
                    </Button>
                    <Button 
                      onClick={calculateLoad} 
                      disabled={calculating}
                      className="bg-blue-600 hover:bg-blue-700 min-w-32"
                    >
                      {calculating ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Calculator className="h-4 w-4 mr-1" />
                      )}
                      Calculate
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Results & 3D View */}
          <div className="space-y-4">
            {/* 3D Visualization */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Maximize2 className="h-5 w-5 text-purple-400" />
                    3D Load View
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowLabels(!showLabels)}
                      className="text-slate-400 hover:text-white"
                    >
                      {showLabels ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      <span className="ml-1 text-xs">Labels</span>
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-[400px] bg-slate-900/50 rounded-lg overflow-hidden">
                  {result?.success ? (
                    <Canvas>
                      <Suspense fallback={null}>
                        <LoadVisualization 
                          result={result} 
                          selectedContainer={selectedContainerIndex}
                          showLabels={showLabels}
                        />
                      </Suspense>
                    </Canvas>
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-500">
                      <div className="text-center">
                        <Container className="h-16 w-16 mx-auto mb-3 opacity-30" />
                        <p>Add cargo items and click Calculate to see 3D visualization</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Container selector tabs */}
                {result?.success && result.containers?.length > 1 && (
                  <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                    {result.containers.map((container, idx) => (
                      <Button
                        key={idx}
                        variant={selectedContainerIndex === idx ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedContainerIndex(idx)}
                        className={selectedContainerIndex === idx 
                          ? "bg-blue-600" 
                          : "border-slate-600 text-slate-300"
                        }
                      >
                        Container {idx + 1}
                      </Button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Results Summary */}
            {result && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    {result.success ? (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-amber-400" />
                    )}
                    Calculation Results
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {result.success ? (
                    <div className="space-y-4">
                      {/* Summary badges */}
                      <div className="flex flex-wrap gap-2">
                        <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                          {result.total_containers} Container{result.total_containers > 1 ? "s" : ""}
                        </Badge>
                        <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                          {result.total_items_loaded} Items Loaded
                        </Badge>
                        {result.total_items_remaining > 0 && (
                          <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">
                            {result.total_items_remaining} Items Remaining
                          </Badge>
                        )}
                        <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                          {result.summary?.avg_volume_utilization}% Volume Used
                        </Badge>
                      </div>

                      {/* Container details */}
                      {result.containers?.map((container, idx) => (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-lg border ${
                            selectedContainerIndex === idx 
                              ? "bg-blue-500/10 border-blue-500/50" 
                              : "bg-slate-700/30 border-slate-600"
                          }`}
                          onClick={() => setSelectedContainerIndex(idx)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-white">
                              {container.container_name}
                            </span>
                            <span className="text-sm text-slate-400">
                              {container.total_items} items
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-400">Volume:</span>
                              <span className="text-blue-300">{container.volume_utilization}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Weight:</span>
                              <span className="text-green-300">{container.weight_utilization}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Total Weight:</span>
                              <span className="text-white">{container.total_weight} kg</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Remaining:</span>
                              <span className="text-white">{container.remaining_items}</span>
                            </div>
                          </div>
                          {container.warnings?.length > 0 && (
                            <div className="mt-2 text-xs text-amber-400">
                              ⚠️ {container.warnings.join(", ")}
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Cargo summary */}
                      {result.cargo_summary?.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-slate-300 mb-2">Cargo Breakdown</h4>
                          <div className="space-y-1">
                            {result.cargo_summary.map((cargo, idx) => (
                              <div key={idx} className="flex justify-between text-sm py-1 border-b border-slate-700">
                                <span className="text-slate-300">{cargo.name} × {cargo.quantity}</span>
                                <span className="text-slate-400">{cargo.volume} m³ / {cargo.total_weight} kg</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto mb-2" />
                      <p className="text-slate-300">{result.summary?.error || "Could not fit cargo"}</p>
                      <p className="text-sm text-slate-500 mt-1">
                        Try different container types or reduce cargo quantities
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Instructions */}
            {!result && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-400 mt-0.5" />
                    <div className="text-sm text-slate-400">
                      <p className="mb-2 font-medium text-slate-300">How to use:</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Select container types you want to consider</li>
                        <li>Add cargo items with dimensions and quantities</li>
                        <li>Click Calculate to optimize loading</li>
                        <li>View 3D placement and utilization stats</li>
                      </ol>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
